import "./index.css";

import axios from "axios";
import React from "react";
import type { AppState } from "@auth0/auth0-react";
import { Auth0Provider } from "@auth0/auth0-react";

// Use API base URL from env (for Docker/deployment); generated client falls back to BASE_PATH otherwise.
if (typeof process.env.REACT_APP_API_URL === "string" && process.env.REACT_APP_API_URL) {
	axios.defaults.baseURL = process.env.REACT_APP_API_URL;
}
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { toast } from "react-toastify";

import App from "./App";
import { Auth0AxiosBridge } from "./Auth/Auth0AxiosBridge";
import { auth0Config, bypassAuth, getAuth0MissingFields } from "./Auth/auth0Config";
import { store } from "./Redux/Index";

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error?.response?.status === 401) {
            return Promise.reject(error);
        }
        let errorMessage: string = error.response.data?.toString() ?? "";

        console.error(errorMessage);
        errorMessage = errorMessage.replace("System.Exception: ", "");

        if (
            error.response.status == 429 ||
            (error.response.data?.toString() ?? "").indexOf("Exceptions.ExceptionToManyRequest") >= 0
        ) {
            toast.warn("To many request!");
        } else {
            toast.error(errorMessage.length > 200 ? errorMessage.substring(0, 199) + "..." : errorMessage);
        }

        return Promise.reject(error);
    }
);

const missingFields = getAuth0MissingFields();
const hasAuth0Config = missingFields.length === 0;
const authorizationParams = {
    redirect_uri: window.location.origin,
    ...(auth0Config.audience ? { audience: auth0Config.audience } : {}),
};
const onRedirectCallback = (appState?: AppState): void => {
    const target = appState?.returnTo ?? window.location.pathname;
    window.history.replaceState({}, document.title, target);
};

// Fetch the runtime config from the API before rendering so URL resolvers
// (storage previews, image picker, etc.) see the right base URLs for the
// current environment. We don't block React on auth — a network failure here
// just falls back to the build-time constants.
const loadRuntimeConfig = async () => {
    try {
        const res = await axios.get("/api/public-config");
        if (res?.data && typeof window !== "undefined") {
            window.__APA_RUNTIME_CONFIG__ = res.data;
        }
    } catch (err) {
        console.warn("Runtime config fetch failed — falling back to build-time defaults.", err);
    }
};

const container = document.getElementById("root");
const renderApp = () => {
    if (!container) return;
    const root = createRoot(container);
    root.render(
        <React.StrictMode>
            {bypassAuth ? (
                <Auth0Provider
                    domain={auth0Config.domain || "bypass.local"}
                    clientId={auth0Config.clientId || "bypass"}
                    authorizationParams={authorizationParams}
                    skipRedirectCallback
                >
                    <Provider store={store}>
                        <App />
                    </Provider>
                </Auth0Provider>
            ) : hasAuth0Config ? (
                <Auth0Provider
                    domain={auth0Config.domain!}
                    clientId={auth0Config.clientId!}
                    authorizationParams={authorizationParams}
                    onRedirectCallback={onRedirectCallback}
                    cacheLocation="localstorage"
                    useRefreshTokens
                >
                    <Auth0AxiosBridge audience={auth0Config.audience} />
                    <Provider store={store}>
                        <App />
                    </Provider>
                </Auth0Provider>
            ) : (
                <div style={{ padding: 24 }}>
                    <h3>Missing Auth0 configuration</h3>
                    <ul>
                        {missingFields.map((field) => (
                            <li key={field}>{field}</li>
                        ))}
                    </ul>
                </div>
            )}
        </React.StrictMode>
    );
};

loadRuntimeConfig().finally(renderApp);
